// The URL stays on the first page. When the visitor leaves, the extra pages they loaded and the
// product at the top of the screen are saved, so Back (or a refresh) rebuilds the same list there.
const STATE_KEY = 'infinite-scroll-state';
const MAX_SAVED_LISTS = 5;
const MAX_RESTORED_PAGES = 10;

function readStates() {
  try {
    return JSON.parse(sessionStorage.getItem(STATE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function writeStates(states) {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify(states));
  } catch (e) {
    // Storage unavailable (private mode): the list just starts from the first page.
  }
}

function savedStateForReturnVisit() {
  const type = performance.getEntriesByType('navigation')[0]?.type;
  if (type !== 'back_forward' && type !== 'reload') return null;
  const state = readStates()[window.location.href];
  return state && Array.isArray(state.urls) && state.urls.length ? state : null;
}

class InfiniteScroll {
  constructor(sentinel, savedState = null) {
    this.sentinel = sentinel;
    this.grid = document.getElementById('product-grid');
    this.sectionId = this.grid?.dataset.id;
    this.nextUrl = document.querySelector('[data-next-page-url]')?.getAttribute('href');
    this.loader = document.getElementById('infinite-scroll-loader');
    this.loadedUrls = [];
    // Products per page, so a saved position knows how many pages it needs back.
    this.pageSize = this.grid?.children.length || 1;
    this.isFetching = false;
    this.restoring = false;
    this.abortController = null;
    this.destroyed = false;

    if (!this.nextUrl || !this.grid) return;

    if (savedState) {
      this.restore(savedState);
    } else {
      this.observe();
    }
  }

  observe() {
    if (!this.nextUrl) {
      this.showEndMessage();
      return;
    }
    this.io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.isFetching) this.loadNext();
    }, { rootMargin: '0px 0px 400px 0px' });
    this.io.observe(this.sentinel);
  }

  async fetchPage(pageUrl, signal) {
    const url = new URL(pageUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  }

  appendPage(pageUrl, doc) {
    doc.querySelectorAll('#product-grid > li').forEach((li) => this.grid.appendChild(li));
    this.nextUrl = doc.querySelector('[data-next-page-url]')?.getAttribute('href') ?? null;
    this.loadedUrls.push(pageUrl);
  }

  async withFetching(work) {
    this.isFetching = true;
    this.abortController = new AbortController();
    this.loader?.classList.add('loading');
    try {
      await work(this.abortController.signal);
    } catch (e) {
      // Network error or abort: scrolling to the sentinel tries again.
      if (e.name !== 'AbortError') console.error('[infinite-scroll]', e);
    } finally {
      this.isFetching = false;
      this.abortController = null;
      this.loader?.classList.remove('loading');
    }
  }

  async loadNext() {
    await this.withFetching(async (signal) => {
      const pageUrl = this.nextUrl;
      this.appendPage(pageUrl, await this.fetchPage(pageUrl, signal));
    });
    if (!this.destroyed && !this.nextUrl) {
      this.io.disconnect();
      this.showEndMessage();
    }
  }

  async restore({ urls, index, offset }) {
    this.restoring = true;
    await this.withFetching(async (signal) => {
      const results = await Promise.allSettled(urls.map((url) => this.fetchPage(url, signal)));
      // Keep pages in order; stop at the first one that failed so the list has no gaps.
      for (const [i, result] of results.entries()) {
        if (result.status !== 'fulfilled') break;
        this.appendPage(urls[i], result.value);
      }
    });
    this.restoring = false;
    if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    if (this.destroyed) return;

    // Scroll to the product that was at the top of the screen, not a pixel offset:
    // late-loading CSS or a rotated phone changes card heights. If not every page came back
    // (or the list was longer than the cap), go to the last product that did.
    const cards = this.grid.children;
    const card = cards[Math.min(index, cards.length - 1)];
    const cardOffset = index < cards.length ? offset : 0;
    requestAnimationFrame(() => window.scrollTo(0, card.getBoundingClientRect().top + window.scrollY - cardOffset));
    this.observe();
  }

  saveState() {
    // Leaving before the list is rebuilt: keep the saved entry for the next Back.
    if (this.restoring) return;
    const states = readStates();
    delete states[window.location.href];
    if (this.loadedUrls.length) {
      const cards = [...this.grid.children];
      // Scrolled past the grid into the footer: return to the last product.
      let index = cards.findIndex((card) => card.getBoundingClientRect().bottom > 0);
      if (index === -1) index = cards.length - 1;
      // Only refetch the pages up to that product; the rest load again on scroll.
      // On the first page there is nothing to refetch, so the browser restores scroll itself.
      const urls = this.loadedUrls.slice(0, Math.min(Math.floor(index / this.pageSize), MAX_RESTORED_PAGES));
      if (urls.length) {
        states[window.location.href] = { urls, index, offset: cards[index].getBoundingClientRect().top };
        // We scroll to the saved product ourselves; stop the browser jumping first.
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      }
    }
    const hrefs = Object.keys(states);
    hrefs.slice(0, Math.max(0, hrefs.length - MAX_SAVED_LISTS)).forEach((href) => delete states[href]);
    writeStates(states);
  }

  showEndMessage() {
    this.sentinel.remove();
  }

  destroy() {
    this.destroyed = true;
    this.io?.disconnect();
    this.abortController?.abort();
    this.isFetching = false;
  }
}

let activeInstance = null;
const sentinel = document.getElementById('infinite-scroll-sentinel');
const savedState = savedStateForReturnVisit();
// Nothing to restore (evicted, storage off, or the list now fits on one page):
// let the browser restore scroll as usual.
if ((!savedState || !sentinel) && 'scrollRestoration' in history) history.scrollRestoration = 'auto';
if (sentinel) activeInstance = new InfiniteScroll(sentinel, savedState);

// facets.js replaces #ProductGridContainer's innerHTML when filters or sort change. Watch it
// here, outside any instance, so a one-page filtered list (no sentinel) doesn't stop the
// watching: clearing the filter later must bring infinite scroll back.
const gridContainer = document.getElementById('ProductGridContainer');
if (gridContainer) {
  new MutationObserver(() => {
    activeInstance?.destroy();
    const newSentinel = document.getElementById('infinite-scroll-sentinel');
    activeInstance = newSentinel ? new InfiniteScroll(newSentinel) : null;
  }).observe(gridContainer, { childList: true });
}

window.addEventListener('pagehide', () => activeInstance?.saveState());
// A back/forward-cache return keeps the page as it was, so hand scrolling back to the browser.
window.addEventListener('pageshow', (event) => {
  if (event.persisted && 'scrollRestoration' in history) history.scrollRestoration = 'auto';
});
